import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const asAgentList = (value) => (Array.isArray(value) ? value : []);

const SearchableAgentSelect = ({
  agents = [],
  value = null,
  onChange,
  isDark = false,
  inputClass = "",
  labelClass = "",
  placeholder = "Tümü",
  requireGsm = false,
  showCompany = false,
  className = "",
  size = "default",
  leftIcon: LeftIcon = null,
  /** Dialog içinde kullanımda popover dialog katmanının üstünde olmalı (dialog z-[1100]). */
  inDialog = false,
  /** Dropdown’da sadece ad soyad (kullanıcı adı / GSM satırı gizli) */
  nameOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const baseAgents = useMemo(() => {
    let list = asAgentList(agents);
    if (requireGsm) list = list.filter((a) => a.gsm);
    return [...list].sort((a, b) => (a.ad_soyad || "").localeCompare(b.ad_soyad || "", "tr"));
  }, [agents, requireGsm]);

  const filteredAgents = useMemo(() => {
    if (!search.trim()) return baseAgents;
    const term = search.toLowerCase().trim();
    return baseAgents.filter(
      (a) =>
        a.ad_soyad?.toLowerCase().includes(term) ||
        a.kullanici_adi?.toLowerCase().includes(term) ||
        (a.gsm && String(a.gsm).includes(term))
    );
  }, [baseAgents, search]);

  const mutedClass = isDark ? "text-slate-500" : "text-gray-400";
  const displayLabel = value?.ad_soyad || placeholder;

  const handleSelect = (agent) => {
    onChange(agent);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between gap-2 font-normal",
            size === "lg" ? "h-10 min-h-10 px-3 text-sm" : "h-7 px-2 text-xs",
            inputClass,
            className
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
            {LeftIcon ? <LeftIcon className="h-4 w-4 shrink-0 opacity-60" /> : null}
            <span className="truncate">{displayLabel}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          inDialog ? "z-[1200]" : "z-[200]",
          "w-[var(--radix-popover-trigger-width)] min-w-[14rem] p-0",
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200",
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className={`p-2 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <div className="relative">
            <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${mutedClass}`} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ad, kullanıcı adı, GSM…"
              className={`h-8 pl-8 text-xs ${inputClass}`}
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-52 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => handleSelect(null)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left ${
              !value
                ? isDark ? "bg-blue-600/20 text-blue-300" : "bg-blue-50 text-blue-700"
                : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
            }`}
          >
            <Check className={`w-3.5 h-3.5 shrink-0 ${!value ? "opacity-100" : "opacity-0"}`} />
            <span>{placeholder}</span>
          </button>
          {filteredAgents.map((agent) => {
            const selected = value?.id === agent.id;
            return (
              <button
                key={agent.id}
                type="button"
                onClick={() => handleSelect(agent)}
                className={`w-full flex items-start gap-2 px-2 py-1.5 rounded text-xs text-left ${
                  selected
                    ? isDark ? "bg-blue-600/20 text-blue-300" : "bg-blue-50 text-blue-700"
                    : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${selected ? "opacity-100" : "opacity-0"}`} />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{agent.ad_soyad}</span>
                  {!nameOnly && (
                    <span className={`block truncate text-[10px] ${labelClass || mutedClass}`}>
                      @{agent.kullanici_adi}
                      {agent.gsm ? ` · ${agent.gsm}` : ""}
                      {showCompany && agent.company_name ? ` · ${agent.company_name}` : ""}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          {filteredAgents.length === 0 && (
            <p className={`px-2 py-3 text-center text-xs ${mutedClass}`}>Sonuç bulunamadı</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableAgentSelect;
